import React, { FC, useEffect, useMemo, useRef, useState, useContext } from "react";
import { useParams } from "react-router-dom";

import { TrainingFolder } from "@/features/training/model/Training/TrainingFolder";
import { CoursesLeftPanelSkeleton } from "@/features/training/pages/training-tab/CoursesSkeletons";
import { TrainingFolderItem } from "@/features/training/pages/training-tab/panel-left/accordion/TrainingFolderItem";
import TrainingActions from "@/features/training/pages/training-tab/panel-left/actions/TrainingActions";
import { StyledTreeView } from "@/features/training/ui";
import ArrowDropDownOutlinedIcon from "@mui/icons-material/ArrowDropDownOutlined";
import ArrowRightOutlinedIcon from "@mui/icons-material/ArrowRightOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import SearchIcon from "@mui/icons-material/Search";
import {
	IconButton,
	InputAdornment,
	Stack,
	TextField,
	Typography,
	alpha,
	useTheme,
} from "@mui/material";
import Box from "@mui/material/Box";

import { useFetchCourseFolders } from "@/service/TrainingQueries";
import { Translate, GetLanguageValueAsString } from "@/app/application/Translate";
import { Language } from "@/model/Language/Language";

import { TranslationContext } from "@/app/providers";



export const TrainingFoldersAccordion: FC = () => {
	const translation: Language | null = useContext(TranslationContext);
	const { courseId } = useParams();
	const theme = useTheme();
	const [expanded, setExpanded] = useState<string[]>([]);
	const [selected, setSelected] = useState<string[]>([]);
	const [search, setSearch] = useState<string>("");
	const treeViewRef = useRef(null);
	const prevExpandedBeforeSearch = useRef<string[] | null>(null);
	const { isLoading, data: folders } = useFetchCourseFolders();

	useEffect(() => {
		// If url has a course id param set its parent folder to be expanded
		const initialExpandedFolderId =
			folders === undefined
				? false
				: folders.find((x) =>
					x.Courses.some((y) => y.Id.toString() === courseId),
				)?.Id;

		if (initialExpandedFolderId) {
			setExpanded([`folder-${initialExpandedFolderId}`]);
		}

		// Also highlight the specific course item in the tree
		if (courseId) {
			setSelected([`course-${courseId}`]);
		}
	}, [courseId, folders]);

	// Scroll the selected item into view once expanded/selected (e.g. from deep links)
	useEffect(() => {
		if (selected.length === 0) return;
		const id = selected[0];
		const timeout = setTimeout(() => {
			const el = document.getElementById(`tree-node-${id}`);
			el?.scrollIntoView?.({ behavior: "smooth", block: "nearest" });
		}, 150);
		return () => clearTimeout(timeout);
	}, [selected, expanded]);

	const normalizedSearch = search.trim().toLowerCase();

	const filteredFolders: TrainingFolder[] = useMemo(() => {
		if (!folders || !normalizedSearch) {
			return folders ?? [];
		}

		return folders
			.map((folder) => {
				const nameMatches =
					folder.Name?.toLowerCase().includes(normalizedSearch) ?? false;

				const filteredCourses =
					folder.Courses?.filter((c) =>
						c.Name?.toLowerCase().includes(normalizedSearch),
					) ?? [];

				const filteredLinks =
					folder.Links?.filter((l) =>
						l.Name?.toLowerCase().includes(normalizedSearch),
					) ?? [];

				if (
					!nameMatches &&
					filteredCourses.length === 0 &&
					filteredLinks.length === 0
				) {
					return null;
				}

				return {
					...folder,
					Courses: filteredCourses,
					Links: filteredLinks,
				};
			})
			.filter((f): f is TrainingFolder => f !== null);
	}, [folders, normalizedSearch]);

	const hasSearch = normalizedSearch.length > 0;
	const hasResults = filteredFolders.length > 0;

	// Auto-expand folders that have matches while searching; restore on clear.
	// Important: do NOT depend on `expanded` here, otherwise user toggles during
	// search get overwritten on every render.
	useEffect(() => {
		if (!hasSearch) {
			if (prevExpandedBeforeSearch.current !== null) {
				setExpanded(prevExpandedBeforeSearch.current);
				prevExpandedBeforeSearch.current = null;
			}
			return;
		}

		if (prevExpandedBeforeSearch.current === null) {
			prevExpandedBeforeSearch.current = expanded.slice();
		}

		const targetIds: string[] = [];
		for (const folder of filteredFolders) {
			if (folder.Id != null) {
				targetIds.push(`folder-${folder.Id}`);
			}
		}

		if (targetIds.length === 0) return;

		// While searching, always fully expand all matching folders so
		// users can see their contents. Original expanded state is
		// restored when the search is cleared.
		setExpanded(targetIds);
	}, [hasSearch, filteredFolders]);

	if (isLoading || folders === undefined) {
		return (
			<Box
				component="section"
				className="accordion-wrapper"
				sx={{ flex: 1, minHeight: 0 }}
			>
				<CoursesLeftPanelSkeleton />
			</Box>
		);
	}

	if (folders.length === 0) {
		return (
			<Box
				component="section"
				className="accordion-wrapper"
				sx={{
					flex: 1,
					minHeight: 0,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					p: 2,
				}}
			>
				<Box
					sx={{
						textAlign: "center",
						p: 3,
						borderRadius: 2,
						border: "1px dashed",
						borderColor: "divider",
						width: "100%",
					}}
				>
					<FolderOutlinedIcon
						sx={{ fontSize: 40, color: "text.disabled", mb: 1.5 }}
					/>
					<Typography variant="body1" color="text.secondary" fontWeight={500}>
						<Translate String="Auto.Features.Training.Pages.TrainingTab.PanelLeft.Accordion.TrainingFoldersAccordion.L178C74.NoFoldersYet">No folders yet</Translate>
					</Typography>
					<Typography variant="body2" color="text.disabled" sx={{ mt: 0.5 }}>
						<Translate String="Auto.Features.Training.Pages.TrainingTab.PanelLeft.Accordion.TrainingFoldersAccordion.L181C73.CreateAFolderToOrganizeYour">Create a folder to organize your courses.</Translate>
					</Typography>
				</Box>
			</Box>
		);
	}

	return (
		<Box
			component="section"
			className="accordion-wrapper"
			sx={{
				flex: 1,
				minHeight: 0,
				overflow: "hidden",
				display: "flex",
				flexDirection: "column",
			}}
		>
			{/* Toolbar with search + Actions */}
			<Stack
				direction="row"
				spacing={1}
				useFlexGap
				alignItems="center"
				justifyContent="space-between"
				sx={{
					flexShrink: 0,
					flexWrap: "wrap",
					px: 2,
					py: 1.75,
					bgcolor: "background.paper",
					borderBottom: `1px solid ${theme.palette.divider}`,
				}}
			>
				<TextField
					size="small"
					placeholder={GetLanguageValueAsString(translation, "Auto.Features.Training.Pages.TrainingTab.PanelLeft.Accordion.TrainingFoldersAccordion.L221C18.SearchFoldersCoursesLinks", "Search folders, courses & links…")}
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					sx={{ flex: "1 1 240px", minWidth: 0 }}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<SearchIcon fontSize="small" aria-hidden />
							</InputAdornment>
						),
						endAdornment:
							search.length > 0 ? (
								<InputAdornment position="end">
									<IconButton
										size="small"
										aria-label={GetLanguageValueAsString(translation, "Auto.Features.Training.Pages.TrainingTab.PanelLeft.Accordion.TrainingFoldersAccordion.L236C22.ClearSearch", "Clear search")}
										onClick={() => setSearch("")}
									>
										<span aria-hidden>✕</span>
									</IconButton>
								</InputAdornment>
							) : undefined,
					}}
				/>

				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 1.5,
						flexShrink: 0,
						flexWrap: "wrap",
						ml: "auto",
						justifyContent: "flex-end",
						width: { xs: "100%", sm: "auto" },
					}}
				>
					<TrainingActions />
				</Box>
			</Stack>

			{/* Scrollable tree area */}
			<Box
				className="wrapper-scroll"
				sx={{
					flex: 1,
					minHeight: 0,
					overflowY: "auto",
					overflowX: "hidden",
					scrollbarGutter: "stable",
					overscrollBehavior: "contain",
					py: 1,
					px: 1.25,
					"&::-webkit-scrollbar": { width: 8 },
					"&::-webkit-scrollbar-track": {
						backgroundColor:
							theme.palette.mode === "dark"
								? alpha(theme.palette.background.default, 0.5)
								: alpha(theme.palette.text.primary, 0.05),
						borderRadius: 8,
					},
					"&::-webkit-scrollbar-thumb": {
						backgroundColor:
							theme.palette.mode === "dark"
								? alpha(theme.palette.text.primary, 0.3)
								: alpha(theme.palette.text.primary, 0.2),
						borderRadius: 8,
					},
					"&:hover::-webkit-scrollbar-thumb": {
						backgroundColor:
							theme.palette.mode === "dark"
								? alpha(theme.palette.text.primary, 0.5)
								: alpha(theme.palette.text.primary, 0.35),
					},
				}}
			>
				{!hasResults && normalizedSearch ? (
					<Box
						sx={{
							p: 3,
							textAlign: "center",
							color: "text.secondary",
						}}
					>
						<Typography variant="body2" fontWeight={600}>
							No results found
						</Typography>
						<Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
							Try different keywords.
						</Typography>
					</Box>
				) : (
					<StyledTreeView
						aria-label="training courses navigator"
						defaultCollapseIcon={<ArrowDropDownOutlinedIcon />}
						defaultExpandIcon={<ArrowRightOutlinedIcon />}
						// @ts-expect-error expandedItems is supported on SimpleTreeView v6
						expandedItems={expanded}
						// @ts-expect-error onExpandedItemsChange is supported on SimpleTreeView v6
						onExpandedItemsChange={(
							_event: React.SyntheticEvent,
							itemIds: string[],
						) => setExpanded(itemIds)}
						// @ts-expect-error selectedItems is supported on SimpleTreeView v6
						selectedItems={selected}
						// @ts-expect-error onSelectedItemsChange is supported on SimpleTreeView v6
						onSelectedItemsChange={(
							_event: React.SyntheticEvent,
							itemIds: string[],
						) => setSelected(itemIds)}
						ref={treeViewRef}
						sx={{
							flexGrow: 1,
							minHeight: 0,
						}}
					>
						{(normalizedSearch ? filteredFolders : folders)?.map(
							(folder, i) => (
								<TrainingFolderItem key={folder.Id} folder={folder} i={i} />
							),
						)}
					</StyledTreeView>
				)}
			</Box>
		</Box>
	);
};
